import {
  type ContentConfig,
  type GlobalConfig,
  type Page,
  useContentConfig,
  useGlobalConfig,
} from '@ad-lucy/context/adData';
import { usePages } from '@ad-lucy/context/pages';
import { TrackerUtils, UriUtils } from '@ad-lucy/utils';
import { useEffect, useRef } from 'react';

import { ApiConfig } from '../../config/api';

export const Reporter = (): null => {
  const { data: globalData } = useGlobalConfig();
  const { data: contentData } = useContentConfig();
  const { currentPage: page } = usePages();
  const observer = useRef<ReportingObserver>();
  const globalDataRef = useRef<GlobalConfig>();
  const contentDataRef = useRef<ContentConfig>();
  const pageRef = useRef<Page>();

  useEffect(() => {
    // Sate values needs to be applied to the refs. Otherwise the observer will not have the latest values.
    globalDataRef.current = globalData;
    contentDataRef.current = contentData;
    pageRef.current = page;
    // callback that will handle intervention reports
    const sendReports = (reports: Report[]): void => {
      const {
        innerWidth = null,
        innerHeight = null,
        outerHeight = null,
        outerWidth = null,
      } = window;
      const params = UriUtils.getQueryParams(window.location.href);
      const reportingData = {
        event: 'Report',
        trigger: reports?.[0]?.type ?? null,
        campaign: globalDataRef.current?.campaign ?? null,
        branch: contentDataRef.current?.branch.id ?? null,
        stage: pageRef.current?.id ?? null,
        environment: ApiConfig.environment,
        navigator: TrackerUtils.getUserAgent(),
        adWidth: innerWidth,
        adHeight: innerHeight,
        windowWidth: outerWidth,
        windowHeight: outerHeight,
        location: {
          lat: params?.lat ?? null,
          lon: params?.lon ?? null,
        },
        reports,
      };

      if (globalDataRef.current?.trackerUrl?.length) {
        navigator.sendBeacon(globalDataRef.current.trackerUrl, JSON.stringify(reportingData));
      }
    };

    const unloadListener = (): void => {
      // pull all pending reports from the queue
      const reports = observer.current?.takeRecords();
      if (reports && reports.length > 0) {
        sendReports(reports);
      }
    };

    if (!observer.current && window?.ReportingObserver?.prototype) {
      observer.current = new ReportingObserver(
        (reports) => {
          sendReports(reports);
        },
        { buffered: true, types: ['intervention'] },
      );
      observer.current?.observe();
      window.addEventListener('pagehide', unloadListener);
    }

    return () => {
      window.removeEventListener('pagehide', unloadListener);
    };
  }, [contentData, globalData, page]);

  return null;
};
